import { CommanderCard } from "./CommanderCard";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { DoctorDto } from "@/src/web-api-client";
import { useLanguage } from "../../i18n/LanguageContext";

export type Commander = {
  id: string;
  name: string;
  position: string;
  title: string;
  specialty: string;
  experience: string;
  avatar: string;
  link: string;
};

export async function fetchCommanders(): Promise<Commander[]> {
  const res = await fetch("/commanders.json", {
    cache: "no-store",
  });
  if (!res.ok) return [];
  return res.json();
}

export function useCommanders() {
  return useQuery({
    queryKey: ["commanders"],
    queryFn: fetchCommanders,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function DoctorSection({
  doctorSchedules,
}: {
  doctorSchedules: DoctorDto[];
}) {
  const { t } = useLanguage();
  const { data: doctors = [] } = useCommanders();
  const [openDoctorId, setOpenDoctorId] = useState<string | number | null>(
    null,
  );

  const handleToggle = (id: string | number) => {
    setOpenDoctorId((prev) => (prev === id ? null : id));
  };

  const scheduleMap = useMemo(() => {
    const map = new Map<string, DoctorDto>();

    for (const doctor of doctorSchedules) {
      const doctorId = String(doctor.doctorId ?? "")
        .trim()
        .toLowerCase();
      if (!doctorId) continue;

      if (
        Array.isArray(doctor.daysThisWeek) &&
        doctor.daysThisWeek.length > 0
      ) {
        map.set(doctorId, doctor);
      }
    }

    return map;
  }, [doctorSchedules]);

  
  return (
    <section className="relative py-10 bg-sub-primary">
      <div className="pointer-events-none absolute h-[450px] w-full -left-10 md:left-20 md:-top-[200px] -top-[150px]">
        <img
          src="/images/logo_108.svg"
          alt=""
          className="absolute -left-10 top-1/3 -translate-y-1/3
                 w-[420px] opacity-[0.07]"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-brand-paper/0 via-brand-primary/60 to-brand-primary" />
      </div>

      <div className="max-w-7xl mx-auto px-8 relative">
        <div className="mb-12 flex flex-col items-center">
          <span className="text-center mb-4 text-xl md:text-2xl lg:text-3xl font-semibold">
            {t("chuyengiakhcn")}
          </span>
          <div className="h-1 w-[200px] bg-green-600" />
        </div>

        {/* Doctor Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {doctors.map((doctor: Commander) => {
            const normalizedCommanderId = String(doctor.id)
              .trim()
              .toLowerCase();
            const matchedSchedule = scheduleMap.get(normalizedCommanderId);
            const hasSchedule = !!matchedSchedule;

            return (
              <CommanderCard
                key={doctor.id}
                {...doctor}
                isOpen={openDoctorId === doctor.id}
                onToggle={handleToggle}
                hasSchedule={hasSchedule}
                doctorForSchedule={matchedSchedule}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
